import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import InstrumentService from './instrument-service';
import "jspdf-autotable";
import { TieredMenu } from "primereact/tieredmenu";
import { useToast } from '../../../components/Toast';
import { exportExcel, exportPDF } from '../../../components/Export';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import { Fieldset } from 'primereact/fieldset';
import { Dropdown } from 'primereact/dropdown';
import ASTInstrumentService from '../../../services/ast/ASTInstrumentService';
import { useDialog } from '../../../components/Dialog';
import { OverlayPanel } from 'primereact/overlaypanel';

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};
// const formatDate = (date: any): string => {
//     if (!date || typeof date === "boolean") return "Invalid Date";
//     return new Date(date).toLocaleDateString("en-GB");
// };

interface RecordFilter {
    [key: string]: string | boolean | Date | null; // Adjust types as needed
}

const InstrumentList: React.FC = () => {
    const [gridDataSource, setGridDataSource] = useState<any[]>([]);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);
    const [isValid, setIsValid] = useState<boolean>(true);
    const { showToast } = useToast();
    const menuRef = useRef<TieredMenu>(null);
    const { showConfirmDialog } = useDialog();
    const [shouldFetch, setShouldFetch] = useState<boolean>(false);
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    // const [qrData, setQrData] = useState<any>(null);

    const [qrCodestatusDropdown, setQrStatusDropdown] = useState<any[]>([]);
    const qrStatusDropdownOptions = useMemo(() => qrCodestatusDropdown, [qrCodestatusDropdown]);

    const [entityDropdown, setEntityDropdown] = useState<any[]>([]);
    const entityDropdownOptions = useMemo(() => entityDropdown, [entityDropdown]);
    const [statusDropdown, setStatusDropdown] = useState<any[]>([]);
    const statusDropdownOptions = useMemo(() => statusDropdown, [statusDropdown]);
    const [recordFilter, setRecordFilter] = useState<RecordFilter>();
    const [page, setPage] = useState(0); // 0 = first page
    const [rows, setRows] = useState(10); // 20 per page (boleh ikut user setting)
    const [totalRecords, setTotalRecords] = useState(0); // jumlah semua data
    const [lastPmVisible, setLastPmVisible] = useState(false);
    const [nextPmVisible, setNextPmVisible] = useState(false);
    const lastPmOp = useRef<OverlayPanel>(null);
    const nextPmOp = useRef<OverlayPanel>(null);

    useEffect(() => {
        const fetchClientState = async () => {

            try {
                const clientStateItem = await InstrumentService.getClientState();
                setClientState(clientStateItem);
                console.log('Fetched client state:', clientStateItem);

                ASTInstrumentService.getDropdown('AST', 'QR_CODE_STATUS').then((data) => {
                    setQrStatusDropdown(data);
                });

                ASTInstrumentService.getDropdown('AST', 'ENTITY').then((data) => {
                    setEntityDropdown(data);
                });

                ASTInstrumentService.getDropdown('AST', 'ASSET_STATUS').then((data) => {
                    setStatusDropdown(data);
                });

                // const searchParam = await InstrumentService.getSearchParam(location.pathname);
                // if (searchParam) {
                //     setRecordFilter(searchParam.recordFilter);
                // } else {
                //     setRecordFilter({ facilityCode: clientStateItem?.facilityCountryCode });
                // }

                const searchParam = await InstrumentService.getSearchParam(location.pathname);
                if (searchParam) {
                    const rf = searchParam.recordFilter || {};

                    // normalize date fields
                    const toDate = (v: any) => (v ? new Date(v) : null);

                    const norm = {
                        ...rf,
                        lastPmDateFrom: toDate(rf.lastPmDateFrom),
                        lastPmDateTo: toDate(rf.lastPmDateTo),
                        nextPmDateFrom: toDate(rf.nextPmDateFrom),
                        nextPmDateTo: toDate(rf.nextPmDateTo),
                        // untuk calendar range (supaya bila buka page balik dia still tunjuk 2 bulan)
                        lastPmDateRange:
                            rf.lastPmDateFrom || rf.lastPmDateTo
                                ? [toDate(rf.lastPmDateFrom), toDate(rf.lastPmDateTo)]
                                : null,
                        nextPmDateRange:
                            rf.nextPmDateFrom || rf.nextPmDateTo
                                ? [toDate(rf.nextPmDateFrom), toDate(rf.nextPmDateTo)]
                                : null,
                    };

                    setRecordFilter(norm);
                } else {
                    setRecordFilter({ facilityCode: clientStateItem?.facilityCountryCode });
                }


            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);


    useEffect(() => {
        if (clientState) {
            const fetchData = async () => {
                try {
                    const params = {
                        ...(recordFilter ?? { facilityCode: clientState.facilityCountryCode }),
                        page: page + 1,
                        pageSize: rows,
                    };

                    InstrumentService.setSearchParam(params, location.pathname);

                    const res = await InstrumentService.findAllInstrument(params);
                    setGridDataSource(res.data);
                    setTotalRecords(res.totalRecords || 0);
                    setShouldFetch(false);
                } catch (error) {
                    showToast(clientState.error);
                }
            };
            fetchData();
        }
    }, [clientState, page, rows, shouldFetch]);


    // const filteredGridDataSource = useMemo(() => {
    //     if (!qrCodeStatus) return gridDataSource;
    //     if (qrCodeStatus === "1") {
    //         // Assigned: has qrData and qrValue
    //         return gridDataSource.filter(row => row.qrdet && row.qrdet !== null);
    //     }
    //     else if (qrCodeStatus === "2") {
    //         // Unassigned: no qrData or no qrValue
    //         return gridDataSource.filter(row => !row.qrdet || row.qrdet === null);
    //     }
    //     return gridDataSource;
    // }, [gridDataSource, qrCodeStatus]);

    const handleSearch = () => {
        // validate range
        const lFrom = recordFilter?.lastPmDateFrom;
        const lTo = recordFilter?.lastPmDateTo;
        const nFrom = recordFilter?.nextPmDateFrom;
        const nTo = recordFilter?.nextPmDateTo;

        if ((lFrom && !lTo) || (!lFrom && lTo)) {
            showToast({
                severity: 'warn',
                summary: 'Date range not complete',
                detail: 'Please select BOTH start and end for Last PM Date.',
            });
            return;
        }

        if ((nFrom && !nTo) || (!nFrom && nTo)) {
            showToast({
                severity: 'warn',
                summary: 'Date range not complete',
                detail: 'Please select BOTH start and end for Next PM Date.',
            });
            return;
        }

        setPage(0);
        setShouldFetch(true);
    };


    const handleInputChange = (e: any) => {
        const { id } = e.target;
        const value = e.value ?? e.target.value;

        // kalau calendar range
        if (id === 'lastPmDateRange') {
            const range = value as Date[] | null;
            setRecordFilter((prev: any) => ({
                ...(prev || {}),
                lastPmDateRange: range,
                lastPmDateFrom: range?.[0] ?? null,
                lastPmDateTo: range?.[1] ?? null,
            }));
            return;
        }

        if (id === 'nextPmDateRange') {
            const range = value as Date[] | null;
            setRecordFilter((prev: any) => ({
                ...(prev || {}),
                nextPmDateRange: range,
                nextPmDateFrom: range?.[0] ?? null,
                nextPmDateTo: range?.[1] ?? null,
            }));
            return;
        }

        // default
        setRecordFilter((prev) => ({
            ...(prev || {}),
            [id]: value,
        }));
    };


    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setRecordFilter({
            ...recordFilter,
            [id]: value
        });
    };


    const handleReset = () => {
        setIsValid(true);
        setRecordFilter({ facilityCode: clientState?.facilityCountryCode });
        setGridDataSource([]);
        setPage(0);           // ✅ reset paginator
        setRows(10);          // ✅ (optional) reset size
        setShouldFetch(true); // trigger refetch
    };

    function handleCreate(): void {
        navigate(`/machine/instrument-edit`);
    }

    function handleUpload(): void {
        navigate(`/machine/instrument-upload`);
    }

    // const handleDeleteInstrument = (rowData: any) => {
    //     showConfirmDialog("Remove Instrument", "Are you sure you want to remove this Instrument?", () => {
    //         // console.log("Delete Instrument", rowData.instrumentId);
    //         InstrumentService.deleteInstrument(rowData.instrumentId).then(() => {
    //             showToast(clientState.success);
    //             setShouldFetch(true);
    //         }).catch((error) => {
    //             clientState.error.detail = error.response?.data?.message || 'Failed to create record.';
    //             showToast(clientState.error);
    //         });
    //     });
    // };

    const handleView = (item: any) => {
        navigate('/machine/instrument-view', { state: { selectedItem: item } });
    };

    const handleEdit = (item: any) => {
        navigate('/machine/instrument-edit', { state: { selectedItem: item } });
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };


    const header = () => {

        const formatDate = (date: any): string => {
            if (!date || typeof date === "boolean") return "Invalid Date";
            return new Date(date).toLocaleDateString("en-GB");
        };

        const fileName = `Instrument List`;

        const exportOptions: any[] = [
            {
                label: "Export to Excel",
                icon: "pi pi-file-excel",
                command: () => exportExcel(fileName),
            },
            {
                label: "Export to PDF",
                icon: "pi pi-file-pdf",
                command: () => exportPDF(fileName),
            },
        ];

        return (
            <div>
                <div className="flex justify-content-between">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.3em' }}>
                        {/* <span className="p-float-label">
                            <InputText type="text" id="inputtext" value={globalFilterValue} onChange={onGlobalFilterChange} />
                            <label htmlFor="inputtext">Keyword Search</label>
                        </span> */}
                    </div>
                    <div>

                        <TieredMenu model={exportOptions} popup ref={menuRef} />
                        <Button
                            label="Excel"
                            icon="pi pi-file-excel"
                            className="p-button-success"
                            style={{ marginRight: '.5em' }}
                            onClick={() => exportExcel(fileName)} />

                        {(
                            (clientState?.country === "Indonesia" && clientState?.userType === "admin") ||
                            (["Qudin MN", "Rachel K", "Naqi B", "Sufian I", "Aida K"].includes(clientState?.userData?.name)
                            )
                        ) && (
                                <>
                                    <Button
                                        label="Upload Excel"
                                        icon="pi pi-upload"
                                        className="p-button-help"
                                        style={{ marginRight: '.5em' }}
                                        onClick={() => handleUpload()}
                                    />

                                    <Button
                                        label="New"
                                        icon="pi pi-plus"
                                        style={{ marginRight: '.5em' }}
                                        onClick={() => handleCreate()}
                                    />
                                </>
                            )}

                    </div>
                </div>
            </div>
        );
    };

    const actionTemplate = (rowData: any) => (
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>


            {rowData.entity === 'PTBGS' && (
                <Button
                    label="Edit"
                    icon="pi pi-pencil"
                    className="p-button-warning p-button-sm"
                    style={{ padding: '5px 5px', fontSize: '10px' }}
                    onClick={() => handleEdit(rowData)}
                />
            )}

            {/* <Button
                label="Delete"
                icon="pi pi-trash"
                className="p-button-danger p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handleDeleteInstrument(rowData)}
            /> */}
            <Button
                label="View"
                icon="pi pi-eye"
                className="p-button-info p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handleView(rowData)}
            />

        </div>

    );



    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Instrument List</h5>
                    <BreadcrumbComponent />
                </div>

                <div className='card'>
                    <Fieldset
                        legend={
                            <span
                                style={{
                                    background: '#9fc4eaef',   // biru
                                    color: '#000000ff',
                                    fontWeight: 'bold',
                                    padding: '5px 10px',
                                    borderRadius: '4px'
                                }}
                            >
                                Search Criteria
                            </span>
                        }
                        className="p-2"
                        toggleable
                        collapsed={false}
                        style={{ background: '##2986cc' }} >
                        <div className="p-fluid form-grid-edit grid">
                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="entity">Entity</label>
                                <div className="f-input">
                                    <Dropdown
                                        id="entity"
                                        value={recordFilter?.entity}
                                        onChange={handleDropdownChange}
                                        options={entityDropdownOptions}
                                        optionLabel="fullDesc"
                                        optionValue="referenceCode"
                                        placeholder="Select One"
                                        showClear
                                    />
                                </div>
                            </div>

                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="snNo">Serial No</label>
                                <div className="f-input">
                                    <InputText
                                        id="snNo"
                                        value={recordFilter?.snNo != null ? String(recordFilter.snNo) : ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="itemDesc">Item Description</label>
                                <div className="f-input">
                                    <InputText
                                        id="itemDesc"
                                        value={recordFilter?.itemDesc != null ? String(recordFilter.itemDesc) : ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="itemCode">Item Code</label>
                                <div className="f-input">
                                    <InputText
                                        id="itemCode"
                                        value={recordFilter?.itemCode != null ? String(recordFilter.itemCode) : ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="installationLocation">Installation Location</label>
                                <div className="f-input">
                                    <InputText
                                        id="installationLocation"
                                        value={recordFilter?.installationLocation != null ? String(recordFilter.installationLocation) : ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            {/* LAST PM DATE */}
                            <div className="f-field-md4">
                                <label className="f-label">Last PM Date</label>
                                <div className="f-input">
                                    <div className="p-inputgroup">
                                        <InputText
                                            value={
                                                recordFilter?.lastPmDateFrom && recordFilter?.lastPmDateTo
                                                    ? `${new Date(recordFilter.lastPmDateFrom as any).toLocaleDateString('en-GB')} - ${new Date(recordFilter.lastPmDateTo as any).toLocaleDateString('en-GB')}`
                                                    : ''
                                            }
                                            readOnly
                                            placeholder="Select Date Range"
                                            onClick={(e) => lastPmOp.current?.toggle(e)}
                                        />
                                        <Button
                                            icon="pi pi-calendar"
                                            onClick={(e) => lastPmOp.current?.toggle(e)}
                                        />
                                    </div>

                                    <OverlayPanel ref={lastPmOp}>
                                        <Calendar
                                            id="lastPmDateRange"
                                            selectionMode="range"
                                            numberOfMonths={2}
                                            value={recordFilter?.lastPmDateRange as any}
                                            inline
                                            dateFormat="dd M yy"
                                            onChange={(e) => {
                                                const range = e.value as Date[] | null;
                                                setRecordFilter((prev: any) => ({
                                                    ...(prev || {}),
                                                    lastPmDateRange: range,
                                                    lastPmDateFrom: range?.[0] ?? null,
                                                    lastPmDateTo: range?.[1] ?? null,
                                                }));
                                                if (range && range[0] && range[1]) {
                                                    lastPmOp.current?.hide();
                                                }
                                            }}
                                        />
                                        <div className="flex justify-end gap-2 mt-2">
                                            <Button
                                                label="Clear"
                                                severity="danger"
                                                text
                                                onClick={() => {
                                                    setRecordFilter((prev: any) => ({
                                                        ...(prev || {}),
                                                        lastPmDateRange: null,
                                                        lastPmDateFrom: null,
                                                        lastPmDateTo: null,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </OverlayPanel>
                                </div>
                            </div>

                            {/* NEXT PM DATE */}
                            <div className="f-field-md4">
                                <label className="f-label">Next PM Date</label>
                                <div className="f-input">
                                    <div className="p-inputgroup">
                                        <InputText
                                            value={
                                                recordFilter?.nextPmDateFrom && recordFilter?.nextPmDateTo
                                                    ? `${new Date(recordFilter.nextPmDateFrom as any).toLocaleDateString('en-GB')} - ${new Date(recordFilter.nextPmDateTo as any).toLocaleDateString('en-GB')}`
                                                    : ''
                                            }
                                            readOnly
                                            placeholder="Select Date Range"
                                            onClick={(e) => nextPmOp.current?.toggle(e)}
                                        />
                                        <Button
                                            icon="pi pi-calendar"
                                            onClick={(e) => nextPmOp.current?.toggle(e)}
                                        />
                                    </div>

                                    <OverlayPanel ref={nextPmOp}>
                                        <Calendar
                                            id="nextPmDateRange"
                                            selectionMode="range"
                                            numberOfMonths={2}
                                            value={recordFilter?.nextPmDateRange as any}
                                            inline
                                            dateFormat="dd M yy"
                                            onChange={(e) => {
                                                const range = e.value as Date[] | null;
                                                setRecordFilter((prev: any) => ({
                                                    ...(prev || {}),
                                                    nextPmDateRange: range,
                                                    nextPmDateFrom: range?.[0] ?? null,
                                                    nextPmDateTo: range?.[1] ?? null,
                                                }));
                                                if (range && range[0] && range[1]) {
                                                    nextPmOp.current?.hide();
                                                }
                                            }}
                                        />
                                        <div className="flex justify-end gap-2 mt-2">
                                            <Button
                                                label="Clear"
                                                severity="danger"
                                                text
                                                onClick={() => {
                                                    setRecordFilter((prev: any) => ({
                                                        ...(prev || {}),
                                                        nextPmDateRange: null,
                                                        nextPmDateFrom: null,
                                                        nextPmDateTo: null,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </OverlayPanel>
                                </div>
                            </div>
                            {/* INSTRUMENT STATUS */}
                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="status">Instrument Status</label>
                                <div className="f-input">
                                    <Dropdown
                                        id="status"
                                        value={recordFilter?.status}
                                        onChange={handleDropdownChange}
                                        options={statusDropdownOptions}
                                        optionLabel="fullDesc"
                                        optionValue="referenceCode"
                                        placeholder="Select One"
                                        showClear
                                    />
                                </div>
                            </div>
                            {/* QR STATUS */}
                            <div className="f-field-md4">
                                <label className="f-label" htmlFor="qrCodeStatus">QR Status</label>
                                <div className="f-input">
                                    <Dropdown
                                        id="qrCodeStatus"
                                        value={recordFilter?.qrCodeStatus}
                                        onChange={handleDropdownChange}
                                        options={qrStatusDropdownOptions}
                                        optionLabel="fullDesc"
                                        optionValue="referenceCode"
                                        placeholder="Select One"
                                        showClear
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <br></br>
                        </div>
                        <div className="col-12 flex align-items-end flex-row-reverse gap-2">
                            <div className='flex '>
                                <Button
                                    label='Reset'
                                    icon="pi pi-undo"
                                    onClick={handleReset}
                                    severity='warning'
                                />
                            </div>
                            <div>
                                <Button
                                    label='Search'
                                    icon="pi pi-search"
                                    onClick={handleSearch}
                                    severity='info'
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br></br>
                    </div>
                    <DataTable
                        value={gridDataSource}
                        showGridlines
                        scrollable
                        // scrollHeight='auto'
                        scrollHeight="500px"
                        paginator
                        rows={rows}
                        first={page * rows}
                        totalRecords={totalRecords}
                        onPage={(e) => {
                            setPage(e.page ?? 0);    // fallback ke 0 kalau undefined
                            setRows(e.rows ?? 10);   // fallback ke 20 kalau undefined
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        lazy // <-- ini wajib untuk server-side paging!
                        filters={filters}
                        globalFilterFields={['instrumentID', 'snNo', 'itemCode', 'itemDesc', 'manufacturerName']}
                        header={header}
                        sortField='modifiedDate'
                        sortOrder={-1}
                        resizableColumns
                        columnResizeMode="expand"  // or "fit"
                        tableStyle={{ tableLayout: 'auto' }}
                    >
                        <Column
                            header="No."
                            style={{ width: '5%' }}
                            body={(_, { rowIndex }) => rowIndex + 1}
                            headerClassName="list-header"
                        />
                        <Column body={actionTemplate} field="action" header="Actions" style={{ width: '20%' }} headerClassName="list-header" />
                        <Column field="snNo" header="Serial No" headerClassName="list-header" />
                        <Column field="entity" header="Entity" headerClassName="list-header" />
                        <Column field="status" header="Status" headerClassName="list-header" />
                        <Column
                            field="qrdet.qrValue"
                            header="QR Status"
                            body={rowData => rowData.qrdet && rowData.qrdet.qrValue ? "Assigned" : "Unassigned"}
                            headerClassName="list-header"
                        />
                        <Column field="qrdet.assignBy" header="Assign By" headerClassName="list-header" />
                        <Column headerClassName="list-header" field="qrdet.assignDate" header="Assign Date" body={(rowData) => rowData?.qrdet?.assignDate ? new Date(rowData.qrdet.assignDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }) : '-'} />
                        <Column field="installationLocation" header="Installation Location" headerClassName="list-header" />
                        <Column field="itemCode" header="Item Code" headerClassName="list-header" />
                        <Column field="itemDesc" header="Item Description" headerClassName="list-header" />
                        <Column field="manufacturerName" header="Manufacturer Name" headerClassName="list-header" />
                        <Column headerClassName="list-header" field="lastPmDate" header="Last PM Date" body={(rowData) => rowData?.lastPmDate ? new Date(rowData.lastPmDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }) : '-'} />
                        <Column headerClassName="list-header" field="nextPmDate" header="Next PM Date" body={(rowData) => rowData?.nextPmDate ? new Date(rowData.nextPmDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }) : '-'} />
                        <Column headerClassName="list-header" field="installDate" header="Installation Date" body={(rowData) => rowData?.installDate ? new Date(rowData.installDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }) : '-'} />
                        <Column headerClassName="list-header" field="warrantyEndDate" header="Warranty End Date" body={(rowData) => rowData?.warrantyEndDate ? new Date(rowData.warrantyEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }) : '-'} />
                        <Column headerClassName="list-header" field="equipType" header="Equipment Type" body={(row) => row?.equipType ? row.equipType : '-'} />
                        <Column field="insertDate" header="Register Date" body={(rowData) => new Date(rowData.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })} headerClassName="list-header" />

                    </DataTable>



                </div>
            </div>
        </div >
    );
};

export default InstrumentList;
